import { useCallback } from 'react';
import { legacy } from '@/shared/api/client';
import { toFiniteNumber } from '@/shared/lib/format';
import { normalizeLeadUniqueIdField } from '@/shared/lib/unique-id-lookup';
import { isMultiStoreLeads } from '@/shared/lib/passbook-utils';
import type { Lead, Passbook } from '@/shared/types';
import type { DataTableFetchParams } from '@/shared/components/data-table';
import { buildBody, type LeadsScopeUser } from './use-leads';

export interface AuditRuleParams {
  maxTransactions: number;
  windowHours: number;
  lookbackDays: number;
}

export interface AuditRule {
  type: 'short-window';
  enabled: boolean;
  autoBlock: boolean;
  params: AuditRuleParams;
}

function passbookHeaders(idPassbook: string) {
  return { headers: { 'X-Passbook-Id': idPassbook } };
}

export async function fetchAuditRules(idPassbook: string): Promise<AuditRule[]> {
  const data = await legacy.get<AuditRule[]>(`/audit-rules/${idPassbook}`, passbookHeaders(idPassbook));
  return Array.isArray(data) ? data : [];
}

export async function saveAuditRules(idPassbook: string, rules: AuditRule[]): Promise<void> {
  await legacy.put(`/audit-rules/${idPassbook}`, { rules }, passbookHeaders(idPassbook));
}

export interface SetLeadBlockedPayload {
  blocked: boolean;
  reason?: string;
  evidence?: string;
  ruleType?: string;
}

export async function setLeadBlocked(idPassbook: string, leadId: string, payload: SetLeadBlockedPayload) {
  await legacy.put(`/user-block/${leadId}`, payload, passbookHeaders(idPassbook));
}

export async function blockAllSuspicious(idPassbook: string): Promise<{ blocked: number }> {
  return legacy.post<{ blocked: number }>(
    `/audit-rules/${idPassbook}/block-suspicious`,
    {},
    passbookHeaders(idPassbook),
  );
}

/** Diagnóstico que o endpoint devolve junto da página de suspeitos. */
export interface SuspiciousMeta {
  computedAt: string;
  cached: boolean;
  truncated: boolean;
  totalSuspicious: number;
  rule: {
    enabled: boolean;
    autoBlock: boolean;
    maxTransactions: number;
    windowHours: number;
    lookbackDays: number;
  };
}

interface SuspiciousResponse {
  data?: Lead[];
  recordsFiltered?: number;
  recordsTotal?: number;
  meta?: SuspiciousMeta;
}

export interface SuspiciousResult {
  rows: Lead[];
  total: number;
  meta?: SuspiciousMeta;
}

/**
 * Listagem de suspeitos — endpoint dedicado, não é mais um filtro do
 * `usersPaginate`. O cálculo da regra fica cacheado no backend; `refresh`
 * força o recálculo.
 */
export function useSuspiciousLeadsFetcher(
  idPassbook: string,
  passbook: Passbook,
  scopeUser?: LeadsScopeUser | null,
) {
  const multi = isMultiStoreLeads(passbook);

  return useCallback(
    async (params: DataTableFetchParams, options?: { refresh?: boolean }): Promise<SuspiciousResult> => {
      const res = await legacy.post<SuspiciousResponse>(
        `/audit/suspicious/${idPassbook}`,
        {
          ...buildBody(idPassbook, params, multi, undefined, scopeUser),
          ...(options?.refresh ? { refresh: true } : {}),
        },
        passbookHeaders(idPassbook),
      );

      return {
        rows: (res.data ?? []).map((row) => {
          const lead = normalizeLeadUniqueIdField(row);
          // Aggregate legado pode devolver total_points como string (Decimal / value / onError).
          const rawPoints = (row as { total_points?: unknown }).total_points;
          if (rawPoints != null && rawPoints !== '') {
            lead.total_points = toFiniteNumber(rawPoints);
          }
          return lead;
        }),
        total: res.recordsFiltered ?? 0,
        meta: res.meta,
      };
    },
    [idPassbook, multi, scopeUser],
  );
}
